import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, MessageSquare, Plus, Send, Reply, Trash2, Type, Image } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MentorshipMatching } from "@/components/community/MentorshipMatching";
import { EventsCalendar } from "@/components/community/EventsCalendar";
import { PollsAndSurveys } from "@/components/community/PollsAndSurveys";
import { ActivityFeed } from "@/components/community/ActivityFeed";
import { AIAssistant } from "@/components/community/AIAssistant";

interface DiscussionReply {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  profiles?: {
    username: string | null;
  } | null;
}

interface Discussion {
  id: string;
  title: string;
  content: string;
  created_at: string;
  user_id: string;
  profiles: {
    username: string | null;
  } | null;
  replies?: DiscussionReply[];
}

interface MoodBoardItem {
  id: string;
  type: 'image' | 'note';
  content: string;
  title?: string;
  color?: string;
}

const DashboardCommunity = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [newDiscussion, setNewDiscussion] = useState({ title: "", content: "" });
  const [isCreating, setIsCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState<Discussion | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  
  // Mood Board state
  const [moodBoardItems, setMoodBoardItems] = useState<MoodBoardItem[]>([]);
  const [moodBoardDialogOpen, setMoodBoardDialogOpen] = useState(false);
  const [itemType, setItemType] = useState<'image' | 'note'>('image');
  const [imageUrl, setImageUrl] = useState("");
  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [noteColor, setNoteColor] = useState("#fef3c7");

  useEffect(() => {
    checkAuth();
    loadMoodBoard();
  }, []);

  const loadMoodBoard = () => {
    const saved = localStorage.getItem("moodBoardItems");
    if (saved) {
      setMoodBoardItems(JSON.parse(saved));
    }
  };

  const saveMoodBoard = (items: MoodBoardItem[]) => {
    localStorage.setItem("moodBoardItems", JSON.stringify(items));
    setMoodBoardItems(items);
  };

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }
    loadDiscussions();
  };

  const loadDiscussions = async () => {
    const { data, error } = await supabase
      .from("discussions")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Error loading discussions");
      setLoading(false);
      return;
    }

    // Fetch profiles
    const userIds = [...new Set(data?.map(d => d.user_id) || [])];
    const { data: profilesData } = await supabase
      .from("profiles")
      .select("id, username")
      .in("id", userIds);

    const profileMap = new Map(profilesData?.map(p => [p.id, p]) || []);
    
    // Fetch replies for each discussion
    const { data: repliesData } = await supabase
      .from("discussion_replies")
      .select("*")
      .order("created_at", { ascending: true });

    const repliesByDiscussion = new Map<string, DiscussionReply[]>();
    repliesData?.forEach(reply => {
      if (!repliesByDiscussion.has(reply.discussion_id)) {
        repliesByDiscussion.set(reply.discussion_id, []);
      }
      repliesByDiscussion.get(reply.discussion_id)?.push({
        ...reply,
        profiles: profileMap.get(reply.user_id) || null,
      });
    });
    
    const discussionsWithProfiles = data?.map(discussion => ({
      ...discussion,
      profiles: profileMap.get(discussion.user_id) || null,
      replies: repliesByDiscussion.get(discussion.id) || []
    })) || [];

    setDiscussions(discussionsWithProfiles);
    setLoading(false);
  };

  const handleCreateDiscussion = async () => {
    if (!newDiscussion.title.trim() || !newDiscussion.content.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsCreating(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }

    const { error } = await supabase
      .from("discussions")
      .insert({
        user_id: session.user.id,
        title: newDiscussion.title,
        content: newDiscussion.content
      });

    if (error) {
      toast.error("Error creating discussion");
    } else {
      toast.success("Discussion created successfully");
      setNewDiscussion({ title: "", content: "" });
      setDialogOpen(false);
      loadDiscussions();
    }
    setIsCreating(false);
  };

  const handleReply = async () => {
    if (!replyContent.trim() || !selectedDiscussion) {
      toast.error("Please enter a reply");
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }

    const { error } = await supabase
      .from("discussion_replies")
      .insert({
        discussion_id: selectedDiscussion.id,
        user_id: session.user.id,
        content: replyContent,
      });

    if (error) {
      toast.error("Error posting reply");
    } else {
      toast.success("Reply posted successfully");
      setReplyContent("");
      setReplyDialogOpen(false);
      loadDiscussions();
    }
  };

  const handleAddMoodBoardItem = () => {
    if (itemType === 'image') {
      if (!imageUrl.trim()) {
        toast.error("Please enter an image URL");
        return;
      }
      const newItem: MoodBoardItem = {
        id: Date.now().toString(),
        type: 'image',
        content: imageUrl,
      };
      saveMoodBoard([...moodBoardItems, newItem]);
      setImageUrl("");
    } else {
      if (!noteTitle.trim() || !noteContent.trim()) {
        toast.error("Please fill in all fields");
        return;
      }
      const newItem: MoodBoardItem = {
        id: Date.now().toString(),
        type: 'note',
        content: noteContent,
        title: noteTitle,
        color: noteColor,
      };
      saveMoodBoard([...moodBoardItems, newItem]);
      setNoteTitle("");
      setNoteContent("");
    }
    setMoodBoardDialogOpen(false);
    toast.success(`${itemType === 'image' ? 'Image' : 'Note'} added to mood board`);
  };

  const handleDeleteMoodBoardItem = (id: string) => {
    saveMoodBoard(moodBoardItems.filter(item => item.id !== id));
    toast.success("Item removed from mood board");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-5xl">
        <div className="flex justify-between items-center mb-6">
          <Link to="/dashboard">
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                New Discussion
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Start a New Discussion</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Title</label>
                  <Input
                    value={newDiscussion.title}
                    onChange={(e) => setNewDiscussion({ ...newDiscussion, title: e.target.value })}
                    placeholder="Enter discussion title"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Content</label>
                  <Textarea
                    value={newDiscussion.content}
                    onChange={(e) => setNewDiscussion({ ...newDiscussion, content: e.target.value })}
                    placeholder="What would you like to discuss?"
                    rows={6}
                  />
                </div>
                <Button
                  onClick={handleCreateDiscussion}
                  disabled={isCreating}
                  className="w-full"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isCreating ? "Creating..." : "Create Discussion"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="discussions" className="mt-8">
          <TabsList className="grid w-full grid-cols-6 mb-8">
            <TabsTrigger value="discussions">Discussions</TabsTrigger>
            <TabsTrigger value="mentorship">Mentorship</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="polls">Polls</TabsTrigger>
            <TabsTrigger value="moodboard">Mood Board</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="discussions">
            <div className="space-y-8">
              <div className="text-center space-y-4 animate-fade-in">
                <h1 className="text-4xl font-bold">Community Discussions</h1>
                <p className="text-xl text-muted-foreground">
                  Connect, share, and learn with fellow members
                </p>
              </div>

              {discussions.length === 0 ? (
                <Card className="border-2 animate-fade-in">
                  <CardContent className="py-12 text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-xl font-semibold mb-2">No Discussions Yet</h3>
                    <p className="text-muted-foreground mb-4">
                      Be the first to start a conversation
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {discussions.map((discussion, index) => (
                    <Card
                      key={discussion.id}
                      className="border-2 hover:border-primary transition-all hover:shadow-lg hover:shadow-primary/20 animate-fade-in"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <CardTitle className="text-xl mb-2">{discussion.title}</CardTitle>
                            <CardDescription className="line-clamp-2">
                              {discussion.content}
                            </CardDescription>
                          </div>
                          <MessageSquare className="w-6 h-6 text-primary flex-shrink-0" />
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {discussion.profiles?.username || "Anonymous"}
                            </span>
                            <span>•</span>
                            <span>
                              {formatDistanceToNow(new Date(discussion.created_at), { addSuffix: true })}
                            </span>
                            <span>•</span>
                            <span>{discussion.replies?.length || 0} replies</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedDiscussion(discussion);
                              setReplyDialogOpen(true);
                            }}
                          >
                            <Reply className="w-4 h-4 mr-2" />
                            Reply
                          </Button>
                        </div>

                        {/* Replies */}
                        {discussion.replies && discussion.replies.length > 0 && (
                          <div className="mt-4 pl-6 border-l-2 border-primary/20 space-y-3">
                            {discussion.replies.map((reply) => (
                              <div key={reply.id} className="space-y-1">
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="font-medium">
                                    {reply.profiles?.username || "Anonymous"}
                                  </span>
                                  <span className="text-muted-foreground">
                                    {formatDistanceToNow(new Date(reply.created_at), { addSuffix: true })}
                                  </span>
                                </div>
                                <p className="text-sm text-muted-foreground">{reply.content}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="mentorship">
            <MentorshipMatching />
          </TabsContent>

          <TabsContent value="events">
            <EventsCalendar />
          </TabsContent>

          <TabsContent value="polls">
            <PollsAndSurveys />
          </TabsContent>

          <TabsContent value="moodboard">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-3xl font-bold">Community Mood Board</h2>
                  <p className="text-muted-foreground mt-2">Share your inspiration and ideas</p>
                </div>
                <Dialog open={moodBoardDialogOpen} onOpenChange={setMoodBoardDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add to Mood Board</DialogTitle>
                    </DialogHeader>
                    <Tabs value={itemType} onValueChange={(v) => setItemType(v as 'image' | 'note')}>
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="image">
                          <Image className="w-4 h-4 mr-2" />
                          Image
                        </TabsTrigger>
                        <TabsTrigger value="note">
                          <Type className="w-4 h-4 mr-2" />
                          Note
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="image" className="space-y-4">
                        <Input
                          placeholder="Enter image URL"
                          value={imageUrl}
                          onChange={(e) => setImageUrl(e.target.value)}
                        />
                        <Button onClick={handleAddMoodBoardItem} className="w-full">
                          Add Image
                        </Button>
                      </TabsContent>
                      <TabsContent value="note" className="space-y-4">
                        <Input
                          placeholder="Note title"
                          value={noteTitle}
                          onChange={(e) => setNoteTitle(e.target.value)}
                        />
                        <Textarea
                          placeholder="Note content"
                          value={noteContent}
                          onChange={(e) => setNoteContent(e.target.value)}
                          rows={4}
                        />
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Color</label>
                          <div className="flex gap-2">
                            {['#fef3c7', '#dbeafe', '#fecaca', '#d9f99d', '#e9d5ff'].map((color) => (
                              <button
                                key={color}
                                className={`w-10 h-10 rounded-full border-2 ${
                                  noteColor === color ? 'border-primary' : 'border-transparent'
                                }`}
                                style={{ backgroundColor: color }}
                                onClick={() => setNoteColor(color)}
                              />
                            ))}
                          </div>
                        </div>
                        <Button onClick={handleAddMoodBoardItem} className="w-full">
                          Add Note
                        </Button>
                      </TabsContent>
                    </Tabs>
                  </DialogContent>
                </Dialog>
              </div>

              {moodBoardItems.length === 0 ? (
                <Card className="p-12 text-center">
                  <p className="text-muted-foreground">Your mood board is empty. Start adding images and notes!</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {moodBoardItems.map((item) => (
                    <Card key={item.id} className="group relative overflow-hidden hover:shadow-lg transition-shadow">
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDeleteMoodBoardItem(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {item.type === 'image' ? (
                        <img
                          src={item.content}
                          alt="Mood board item"
                          className="w-full h-64 object-cover"
                        />
                      ) : (
                        <div
                          className="p-6 min-h-[200px]"
                          style={{ backgroundColor: item.color }}
                        >
                          <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                          <p className="text-sm">{item.content}</p>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <ActivityFeed />
          </TabsContent>
        </Tabs>

        <AIAssistant />

        {/* Reply Dialog */}
        <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reply to: {selectedDiscussion?.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write your reply..."
                rows={6}
              />
              <Button onClick={handleReply} className="w-full">
                <Send className="w-4 h-4 mr-2" />
                Post Reply
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DashboardCommunity;
